/**
 * Redux module for supporting generalized Finsemble UX functions such as UI Resolver, etc.
 */
/**
 * Notes. Because reducers use Immer, you can't just console.log pieces of your store. You'll get a bunch of proxy
 * stuff from the WritableDraft<> template. Wrap your data in unImmer() to print it out normally.
 */

import { ActionReducerMapBuilder, unImmer } from "../../../../common/redux/createReducer";
import { actions } from "./actions";
import { WindowServiceStore, WindowServiceUXStore, SafetyTimeout } from "../types";
import { sendCommandToWindowService } from "./main";
import { getRemoteReduxStore } from "../../common/store";
import { Rectangle } from "../../../../FEA";
import { WindowServiceStoreInstance } from "../localTypes";

/**
 * This is safe to use in any function that is an effect from a reducer, since the store is guaranteed to
 * already exist. We use this instead of the getStore() from ../store.ts in order to avoid a circular dependency.
 */
const getStore = () => getRemoteReduxStore<WindowServiceStoreInstance>("@@finsemble/remoteRedux/WindowServiceStore");

const getInitialState = (): WindowServiceUXStore => {
	return {
		scrimWindowName: null,
		resolverWindowName: null,
		highlightedWindowName: null,
		scrimSafetyTimeout: null,
	};
};

/**
 * Displays the scrim at the requested bounds. A safety timer is started to ensure that the window is hidden after X seconds, even
 * if mouseout/blur events are somehow missed.
 *
 * The scrim is stacked so that its z-index is greater than the window it is highlighting. This is accomplished using bringToFront,
 * except when the window to be highlighted is set to alwaysOnTop. In that circumstance, the trick of toggling AOT for the scrim
 * and then for the resolver is used to force a z-index.
 *
 * This behavior causes the resolver to briefly lose focus, so that an additional setScrim() action is dispatched. This action is
 * ignored by the reducer by virtue of it referencing the same window as that which has already been highlighted.
 */
const showScrimOverWindow = async ({
	bounds,
	scrimName,
	windowName,
	resolverName,
	safetyTimeout,
	isAOT,
}: {
	bounds: Rectangle;
	scrimName: string;
	windowName: string;
	resolverName: string | null;
	safetyTimeout: SafetyTimeout;
	isAOT: boolean;
}) => {
	if (safetyTimeout) clearTimeout(safetyTimeout);
	// show must come first because otherwise it opens up a race between this set of window actions and a possible reversal from a hide event
	// Those reversals can come extremely quickly, as a series of rapid mouseover events.
	await sendCommandToWindowService(scrimName, "show");
	await sendCommandToWindowService(windowName, "bringToFront");
	await sendCommandToWindowService(scrimName, "setBounds", {
		bounds: {
			top: bounds.y,
			left: bounds.x,
			width: bounds.width,
			height: bounds.height,
		},
	});

	// We use different logic for an AOT window vs. a normal window because the mouseover effects are a wee bit smoother when
	// we don't have to do the z-index stacking
	if (isAOT) {
		/**
		 * When highlighting a window that is AOT we must build a z-index stack. We do this by flipping the AOT
		 * state of our scrim and then our resolver so that Electron stacks them (the most recent AOT is always the highest)
		 */
		await sendCommandToWindowService(scrimName, "setAlwaysOnTop", { alwaysOnTop: false });
		await sendCommandToWindowService(scrimName, "setAlwaysOnTop", { alwaysOnTop: true });
		if (resolverName) await sendCommandToWindowService(resolverName, "setAlwaysOnTop", { alwaysOnTop: false });
		if (resolverName) await sendCommandToWindowService(resolverName, "setAlwaysOnTop", { alwaysOnTop: true });
	} else {
		await sendCommandToWindowService(scrimName, "setAlwaysOnTop", { alwaysOnTop: false });
		await sendCommandToWindowService(scrimName, "bringToFront");
	}

	// Whenever we display the scrim we set a safety timer that will hide the scrim after 5 seconds
	// This ensures that the scrim never stays displayed accidentally, such as if a mouseout event was missed
	const timeout = setTimeout(() => {
		getStore()?.dispatch(actions.hideScrim());
		getStore()?.dispatch(actions.setScrimSafetyTimeout(null));
	}, 5000);

	getStore()?.dispatch(actions.setScrimSafetyTimeout(timeout));
};

const unsetSafetyTimer = (timer: SafetyTimeout) => {
	if (timer) clearTimeout(timer);
};

/**
 * Finds a windowName given an app (component) type. It will return the first one found.
 */
const findWindowForAppType = (state: WindowServiceStore, appType: string): string | null => {
	const entries = Object.entries(state.main.launchData).find(
		([, launchData]) => launchData.windowDescriptor.componentType === appType
	);
	if (!entries) return null;
	return entries[0];
};

const addReducerCases = (builder: ActionReducerMapBuilder<WindowServiceStore>) => {
	builder
		/**
		 * Highlights the requested window with Finsemble's scrim. If the window is a hidden
		 * tab then it will be promoted.
		 */
		.addCase(actions.showScrim, (state, action, cmd) => {
			const windowName = action.payload;
			if (windowName === state.ux.highlightedWindowName) return;
			state.ux.highlightedWindowName = windowName;
			const { bounds, visible } = state.main.windows[windowName]?.physicalWindowState;

			if (!bounds) return;

			/*
			If visible=true (the window is visible), the scrim will show over it.
			If visible=false, but autoShow=true, then the the window WILL BE visible by the time 
			we show the scrim over it.
			If the window isn't visible, and autoShow=false, then we don't show the scrim, because there's no
			window to highlight.
			*/
			if (visible === false && state.main.launchData[windowName].windowDescriptor.autoShow === false) {
				return;
			}

			// Find the scrim and resolver among all our windows, if we haven't already
			if (!state.ux.scrimWindowName) state.ux.scrimWindowName = findWindowForAppType(state, "HighlightScrim");
			if (!state.ux.resolverWindowName) state.ux.resolverWindowName = findWindowForAppType(state, "FDC3Resolver");

			// Set the scrim's bounds to the same as the window
			if (state.ux.scrimWindowName) {
				cmd.run(showScrimOverWindow, {
					args: [
						{
							bounds: unImmer(bounds),
							windowName,
							scrimName: state.ux.scrimWindowName,
							resolverName: state.ux.resolverWindowName,
							safetyTimeout: state.ux.scrimSafetyTimeout,
							isAOT: state.main.windows[windowName]?.physicalWindowState?.isAlwaysOnTop ?? false,
						},
					],
				});
			}
		})
		/**
		 * Removes the scrim and clears the safety timeout
		 */
		.addCase(actions.hideScrim, (state, action, cmd) => {
			cmd.run(unsetSafetyTimer, {
				args: [state.ux.scrimSafetyTimeout],
			});
			state.ux.scrimSafetyTimeout = null;
			state.ux.highlightedWindowName = null;
			if (state.ux.scrimWindowName) {
				cmd.run(sendCommandToWindowService, {
					args: [state.ux.scrimWindowName, "hide"],
				});
			}
		})
		.addCase(actions.setScrimSafetyTimeout, (state, action) => {
			state.ux.scrimSafetyTimeout = action.payload;
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
};
